import { EllipsisVerticalIcon } from '@heroicons/react/24/solid';
import Page from '../../../components/PageWrapper';
import { useMemo, useState } from 'react';
import Loader from '../../../components/Loader';
import GTable from '../../../components/elements/GTable';
import { getAllQuotes } from '../../../api/requests';
import { Quote } from '../../../models/quote/Quote';
import PopOnMe from '../../../components/elements/poponme/PopOnMe';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ITablePaginationData, ITableRowAction } from '../../../components/elements/GTable/GTable';
import { getFormattedLeadSource, getFormattedPhoneNumber, getPhoneNumberFromShortCodeFormat, getVehicleProtectionFromKey, showToast } from '../../../utils/miscFunctions';
import usePolicy from '../../../hooks/policyHook';
import { Policy } from '../../../models/policy/Policy';
import useModal from '../../../hooks/modalHook';
import useRoute from '../../../hooks/routesHook';
import moment from 'moment';
import useUserAccount from '../../../hooks/userAccountHook';
import useLayoutHook from '../../../hooks/layoutHook';
import { useQuery } from '@tanstack/react-query';
import { Eye, FilePlus, Shield } from 'lucide-react';
import PolicyStatus from '../../../components/elements/PolicyStatus';
import Button from '../../../components/form-control/Button';

const Quotes = (): JSX.Element => {
  const [quotes, setQuotes] = useState<any>([]);
  const { screenWidth } = useLayoutHook();
  const { PolicyModalComponent } = usePolicy();
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState<number>(searchParams.get('page') ? Number(searchParams.get('page')) : 0);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchString, setSearchString] = useState<string>();
  const [quotesPaginationData, setQuotesPaginationData] = useState<ITablePaginationData>({
    total: 0,
    per_page: 0,
    totalPages: 0,
    initialPage: currentPage ? currentPage : 0
  });
  const [showPerPage, setShowPerPage] = useState<number>(100);
  const { ModalComponent, updateModalStates, closeModal } = useModal();
  const { generatePolicyFromQuote } = usePolicy();
  const navigate = useNavigate();
  const { getDashboardBase } = useRoute();
  const { user } = useUserAccount();
  const phone = searchString;

  const getQuotes = async (page = currentPage) => {
    try {
      const response = await getAllQuotes(page, showPerPage, phone);
      const Quote = response?.data?.content;
      setQuotes(Quote);
      const from = response?.data.pageable?.offset + 1;
      const to =
        from + response?.data?.size > response?.data?.totalElements
          ? response?.data?.totalElements
          : from + response?.data?.size - 1;

      setQuotesPaginationData({
        ...quotesPaginationData,
        ...{
          per_page: response?.data?.size,
          total: response?.data?.totalElements,
          totalPages: response?.data?.totalPages,
          from: from,
          to: to
        }
      });
    } catch (error) { }
    finally {
      setLoading(false);
    }
  };

  const getTableRowActions = (data: any): ITableRowAction[] => {
    const actions = [
      {
        label: 'View',
        icon: <Eye className="h-4 w-4" />,
        callback: (quote: Quote) => {
          navigate(`${getDashboardBase()}/quotes/${quote.ref}`, { replace: true });
        },
        data: data
      }
    ];

    if (user.isAdministrator()) {
      // if (user.isAdmin()) {
      actions.push({
        label: 'Create policy from quote',
        icon: <FilePlus className="h-4 w-4" />,
        callback: (quote: Quote) => {
          handleCreatePolicy(quote);
        },
        data: data
      });
    }
    return actions;
  };

  const mobileColumns = useMemo(
    () => [
      {
        Header: 'M',
        columns: [
          {
            Header: 'Name',
            accessor: 'firstName',
            Cell: (row: any) => {
              return !row.row.original.firstName && !row.row.original.lastName ? (
                <span>-</span>
              ) : (
                <span>
                  {row.row.original.firstName} {row.row.original.lastName}
                </span>
              );
            }
          },
          {
            Header: 'Source',
            accessor: 'source',
            Cell: (row: any) => {
              return <span className="">{row.cell.value ? getFormattedLeadSource(row.cell.value) : '-'}</span>;
            }
          },
          {
            Header: 'Phone',
            accessor: 'phone',
            Cell: (row: any) => {
              return (
                <span>
                  {row.cell.value ? getPhoneNumberFromShortCodeFormat(row.cell.value) : '-'}
                </span>
              );
            }
          },
          {
            Header: 'Protection Type',
            accessor: 'protectionType',
            Cell: (row: any) => {
              return (
                <span>
                  {' '}
                  {row.cell.value ? (
                    <>{getVehicleProtectionFromKey(row.cell.value)?.title}</>
                  ) : (
                    '-'
                  )}
                </span>
              );
            }
          },
          {
            Header: 'Date',
            accessor: 'paymentPlan',
            Cell: (row: any) => {
              return (
                <span>
                  {row.cell.value
                    ? moment(row.cell.value.createdOn).format('MMMM Do YYYY, h:mm:ss a')
                    : '-'}
                </span>
              );
            }
          },
          {
            id: 'Action',
            Cell: (row: any) => {
              return (
                <div className="cursor-pointer flex justify-center">
                  <PopOnMe actions={getTableRowActions(row.row.original)}>
                    <EllipsisVerticalIcon className="h-4 w-4" />
                  </PopOnMe>
                </div>
              );
            }
          }
        ]
      }
    ],
    []
  );

  const columns = useMemo(
    () => [
      {
        Header: 'M',
        columns: [
          {
            Header: 'Name',
            accessor: 'firstName',
            Cell: (row: any) => {
              return !row.row.original.firstName && !row.row.original.lastName ? (
                <span>-</span>
              ) : (
                <span>
                  {row.row.original.firstName} {row.row.original.lastName}
                </span>
              );
            }
          },
          {
            Header: 'Source',
            accessor: 'source',
            Cell: (row: any) => {
              return <span className="">{row.cell.value ? getFormattedLeadSource(row.cell.value) : '-'}</span>;
            }
          },
          {
            Header: 'Phone',
            accessor: 'phone',
            Cell: (row: any) => {
              return (
                <span>
                  {row.cell.value ? getPhoneNumberFromShortCodeFormat(row.cell.value) : '-'}
                </span>
              );
            }
          },
          {
            Header: 'Protection Type',
            accessor: 'protectionType',
            Cell: (row: any) => {
              return (

                row.cell.value ? <PolicyStatus
                  status={String(getVehicleProtectionFromKey(row.cell.value)?.title)}

                  classes="px-2"
                /> : '-'
              );
            }
          },
          {
            Header: 'Date',
            accessor: 'paymentPlan',
            Cell: (row: any) => {
              return (
                <span>
                  {row.cell.value
                    ? moment(row.cell.value.createdOn).format('MMMM Do YYYY, h:mm:ss a')
                    : '-'}
                </span>
              );
            }
          },
          {
            id: 'Action',
            Cell: (row: any) => {
              return (
                <div className="cursor-pointer flex justify-center">
                  <PopOnMe actions={getTableRowActions(row.row.original)}>
                    <EllipsisVerticalIcon className="h-4 w-4" />
                  </PopOnMe>
                </div>
              );
            }
          }
        ]
      }
    ],
    []
  );

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setSearchParams({ page: String(page) });
    getQuotes(page);
  };

  const handleCreatePolicy = (quote: Quote) => {
    updateModalStates({
      show: true,
      size: 'md',
      children: <Loader message="Just a sec..." size="2xl" />
    });

    if (!quote.phone) {
      showToast.error('Phone number is required to create a policy from quote.');
      closeModal();
      return;
    }

    generatePolicyFromQuote(quote.id)
      .then((policy: Policy) => {
        if (policy) {
          showToast.success(
            'Policy created successfully from quote. Proceed to provide policy details.',
            
          );
          navigate(`${getDashboardBase()}/policies/${policy.ref}/edit`, { replace: true });
        }
      })
      .catch((error: any) => {
        closeModal();
        showToast.error(error.message ?? 'Failed to create policy from quote. Please try again later');
      });
  };

  useQuery({
    queryKey: ['getQuotes', currentPage, showPerPage, searchString, quotes],
    queryFn: () => getQuotes(),
  });


  return (
    <Page title="Quotes">
      <div className="bg-white dark:bg-darkBg w-full p-5 min-h-full rounded-lg shadow-md">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center">
          <div className="dark:text-darkBg-mainText ">
            <div className="inline-flex items-center gap-2 mb-2">
              <div className="h-8 w-2 bg-gradient-to-b from-yellow-500 to-yellow-700 rounded"></div>
              <h2 className="font-semibold text-3xl">Quotes</h2>
            </div>
            <p className="text-sm text-slate-400">
              View a list of all quotes and associated details.{' '}
            </p>
          </div>
          <div className="sm:flex justify-end  gap-4 pt-2 md:pt-0">
            <Button
              bgColor="primary"
              label={'New Quote'}
              icon={<Shield className='h-5 w-5' />}
              onClick={() => navigate(`${getDashboardBase()}/quotes/calculate`)}
            />
          </div>
        </div>

        <div className="overflow-x-auto mt-4 h-full">
          <GTable
            selectionChanged={() => {
              // setSlcMember(selection);
            }}
            columns={screenWidth <= 550 ? mobileColumns : columns}
            data={quotes}
            onSearch={(searchString: string) =>
              searchString.charAt(0) === '0'
                ? setSearchString(getFormattedPhoneNumber(searchString))
                : setSearchString(searchString)
            }
            classes="h-fit"
            loading={loading}
            searchPlaceholder={'Search by phone...'}
            tableType={'quotes'}
            paginationData={quotesPaginationData}
            onPageChange={handlePageChange}
            showPerPage={showPerPage}
            onShowPerPageChange={(page: number) => setShowPerPage(page)}
            onRowClick={(quote: Quote) => {
              navigate(`${getDashboardBase()}/quotes/${quote.ref}`, { replace: true });
            }}
          // TO DO: Uncomment this when the implementation for filtering tables by policy status is done.
          // topActions={getTableTopActions()}
          />
        </div>
      </div>
      <ModalComponent />
      <PolicyModalComponent />
    </Page>
  );
};

export default Quotes;
